import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoginInputSchema, type LoginInput } from "@clinic/types";
import { useQueryClient } from "@tanstack/react-query";
import { api, IS_DEMO } from "@/lib/api";
import { LogoFull } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { FieldError, Input, Label } from "@/components/ui/input";

const DEMO_ACCOUNTS = [
  { label: "Admin", email: "admin@otzemclinic.com" },
  { label: "Doctor", email: "doctor@otzemclinic.com" },
  { label: "Staff", email: "staff@otzemclinic.com" },
];

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);

  const signIn = async (values: LoginInput) => {
    setServerError(null);
    try {
      const res = await api.auth.login(values);
      queryClient.setQueryData(["me"], res.user);
      navigate({ to: "/" });
    } catch (err) {
      setServerError(err instanceof Error ? err.message : "Login failed");
    }
  };

  const form = useForm<LoginInput>({
    resolver: zodResolver(LoginInputSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = form.handleSubmit(signIn);

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1 items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="mb-10 flex justify-center">
            <LogoFull iconSize={72} />
          </div>
          <form
            onSubmit={onSubmit}
            className="space-y-4 rounded-card border bg-bg-secondary p-6 shadow-card"
            style={{ borderColor: "var(--accent-subtle)" }}
          >
            <h1 className="text-lg">Staff sign in</h1>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@otzemclinic.com"
                {...form.register("email")}
              />
              <FieldError message={form.formState.errors.email?.message} />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••••"
                {...form.register("password")}
              />
              <FieldError message={form.formState.errors.password?.message} />
            </div>
            {serverError && (
              <p className="rounded-input border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
                {serverError}
              </p>
            )}
            <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
              Sign in
            </Button>
          </form>

          {IS_DEMO && (
            <div
              className="mt-4 rounded-card border bg-bg-secondary/60 p-4"
              style={{ borderColor: "var(--accent-subtle)" }}
            >
              <div className="mb-2 flex items-center justify-center gap-2 text-xs font-medium text-accent-light">
                <span className="h-1.5 w-1.5 rounded-pill bg-accent" />
                Demo mode — one-click sign in
              </div>
              <div className="grid grid-cols-3 gap-2">
                {DEMO_ACCOUNTS.map((a) => (
                  <Button
                    key={a.email}
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => signIn({ email: a.email, password: "Password123!" })}
                  >
                    {a.label}
                  </Button>
                ))}
              </div>
              <p className="mt-2 text-center text-[11px] text-text-tertiary">
                Sample data · no server needed
              </p>
            </div>
          )}

          <p className="mt-4 text-center text-xs text-text-tertiary">
            Access restricted to clinic staff.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
